import { ClassName, Theme } from '@busybusy/webapp-react-ui';
import { PhotoIcon } from 'assets/icons';
import classNames from 'classnames';
import { dashboardCardBarChartGradientBlueLightPurple } from 'components/domain/dashboard/dashboard-card-chart/DashboardBarChart/constants/constants';
import DashboardCardBarChart from 'components/domain/dashboard/dashboard-card-chart/DashboardBarChart/DashboardBarChart';
import { IDashboardSettingsItem } from 'components/domain/dashboard/DashboardSettingsItem/DashboardSettingsItem';
import DashboardContentCard from 'components/foundation/dashboard/DashboardContentCard/DashboardContentCard';
import useDashboardGraphDates from 'containers/dashboard/hooks/useDashboardGraphDates';
import { convertDashboardTimeFrameToTimeRangeType, getDashboardTimeFrameTitle } from 'containers/dashboard/util/utils';
import { getDashboardNavigationRange } from 'containers/scheduling/utils/utils';
import { gql } from 'graphql-request';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useGPSDisabledNavigation from 'hooks/navigation/useGPSDisabledNavigation';
import { t } from 'i18next';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ITimeRange from 'types/TimeRange';
import { useFeatureFlags } from 'utils/features';
import DashboardProUpgradeCard from '../DashboardProUpgradeCard/DashboardProUpgradeCard';
import useDashboardCardTotal from '../hooks/useDashboardCardTotal';

export interface IGPSDisabledDashboardCardProps {
  className?: ClassName;
  theme: Theme.DARK | Theme.LIGHT;
  setting: IDashboardSettingsItem;
  onHide: () => void;
}

const GPSDisabledDashboardCard = (props: IGPSDisabledDashboardCardProps) => {
  const { className, theme, setting, onHide } = props;

  const isPro = useFeatureFlags('PRO');

  const timeFrame = setting.options?.time;
  const { timeRange, timeRanges } = useDashboardGraphDates(timeFrame !== 'daily' ? timeFrame : 'pastSeven')!;

  const getData = useGPSDisabledDashboardCardData(timeRanges);
  const [barCounts, setBarCounts] = useState<IDashboardRangeCount[] | null>(null);

  const zippedValues = timeRanges.map((range, index) => ({
    day: range.startTime,
    value: barCounts?.[index]?.count ?? 0,
  }));

  const total = useDashboardCardTotal(
    timeRanges,
    null,
    null,
    zippedValues,
    setting.options?.time ?? 'payPeriod',
    false
  );

  const navigateToReport = useGPSDisabledNavigation();

  useEffect(() => {
    if (!isPro) return;
    getData().then(setBarCounts);
  }, [getData, isPro]);

  function handleNavigation() {
    const navRange = getDashboardNavigationRange(timeRange, timeRanges, setting.options?.time ?? 'payPeriod', 'utc');

    navigateToReport({
      start_date: navRange.startTime.toISODate(),
      end_date: navRange.endTime.toISODate(),
      time_range_type: convertDashboardTimeFrameToTimeRangeType(setting.options?.time ?? 'payPeriod'),
    });
  }

  const classes = classNames('gps-disabled-dashboard-card', className);

  return (
    <>
      {isPro ? (
        <DashboardContentCard
          header={total}
          theme={theme}
          subheaderValue={getDashboardTimeFrameTitle(t('GPS Disabled'), setting.options?.time ?? 'monthly')}
          className={classes}
          rightLabel={t('Details')}
          onRightLabelClick={handleNavigation}
        >
          <DashboardCardBarChart
            data={zippedValues}
            dataKey="value"
            valueType="raw"
            timeFrame={timeFrame}
            theme={theme}
            {...dashboardCardBarChartGradientBlueLightPurple}
          />
        </DashboardContentCard>
      ) : (
        <DashboardProUpgradeCard title={setting.title} svg={PhotoIcon} theme={theme} onHide={onHide} />
      )}
    </>
  );
};

// Backed by the custom `dashboard.gpsDisabledCounts` resolver, which scopes by manageGpsApproval and
// counts disabled GPS statuses per bar server-side — replacing the old getAll pagination through
// every disabled status.
const GPS_DISABLED_DASHBOARD_CARD_QUERY = gql`
  query GpsDisabledDashboardCard($ranges: [DashboardRangeInput!]!) {
    dashboard {
      gpsDisabledCounts(ranges: $ranges) {
        startTime
        endTime
        count
      }
    }
  }
`;

interface IDashboardRangeCount {
  startTime: string;
  endTime: string;
  count: number;
}

// Mirrors the report's local-day grouping: shift each UTC bar boundary by the local offset so the
// counts bucket by local day exactly as the card did with assignItemsByCreationToRanges.
function toShiftedTimestamp(seconds: number): string {
  const dateTime = DateTime.fromSeconds(seconds, { zone: 'utc' });
  // `seconds` is always a valid epoch, so toISO never returns null here; assert to match the dashboard
  // query's non-null Timestamp! range boundaries.
  return dateTime.minus(dateTime.toLocal().offset * 60 * 1000).toISO({ suppressSeconds: true })!;
}

export function useGPSDisabledDashboardCardData(timeRanges: Array<ITimeRange<number>>) {
  const graphQLClient = useGraphQLClient();

  // timeRanges is value-stable, but key on its values so the query doesn't refire on identity churn.
  const rangesKey = JSON.stringify(timeRanges);
  const ranges = useMemo(
    () =>
      timeRanges.map((range) => ({
        startTime: toShiftedTimestamp(range.startTime),
        endTime: toShiftedTimestamp(range.endTime),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rangesKey]
  );

  return useCallback(async (): Promise<IDashboardRangeCount[]> => {
    const result = await graphQLClient.request<{ dashboard: { gpsDisabledCounts: IDashboardRangeCount[] } }>({
      document: GPS_DISABLED_DASHBOARD_CARD_QUERY,
      variables: { ranges },
    });

    return result.dashboard.gpsDisabledCounts;
  }, [graphQLClient, ranges]);
}

export default GPSDisabledDashboardCard;
